import { InvoiceStatus, InvoiceType } from '@vue-app/modules/invoices/types.js'
import { INVOICE_TYPE_ABBREVIATIONS, API_TO_UI_TYPE, BILL_TYPE_CODE_TO_ID } from '@vue-app/modules/invoices/constants.js'
import { formatDateTime } from "@/utils/date.js";

export function mapInvoiceFromApi(apiInvoice) {
	return {
		id: String(apiInvoice.bill_id),
		number: apiInvoice.bill_number,

		status: apiInvoice.status_code.toLowerCase(),
		createdAt: apiInvoice.created_at,
		date: formatDateTime(apiInvoice.created_at, 'uk-UA'),
		dueDate: apiInvoice.due_date?.split('T')[0],

		currency: apiInvoice.currency_code,
		type: API_TO_UI_TYPE[apiInvoice.type] || InvoiceType.STANDARD,
		dic_bill_type_id: apiInvoice.dic_bill_type_id,

		senderName: '',
		senderDetails: '',

		recipient: apiInvoice.recipient || null,

		items: [],
		paymentPurpose: apiInvoice.payment_purpose || '',
		partialPayment: Boolean(apiInvoice.partialPayment),
		note: '',

		transactions: [],

		totalAmount: apiInvoice.total_amount / 100,
		paidAmount: apiInvoice.total_pay_amount / 100,
		amountDue: apiInvoice.amount_due / 100,
	}
}

export function mapInvoiceFromDetailsApi(apiResponse) {
	const data = apiResponse.data
	return {
		id: String(data.billId),
		number: data.billNumber,

		status: data.status.toLowerCase(),
		createdAt: data.issueDate,
		date: formatDateTime(data.issueDate, 'uk-UA'),
		dueDate: data.dueDate?.split('T')[0] || null,

		currency: data.currency,
		type: API_TO_UI_TYPE[data.type] || InvoiceType.STANDARD,
		dic_bill_type_id: data.dic_bill_type_id,

		senderName: data.senderName || '',
		senderDetails: data.senderDetails || '',

		recipient: data.recipient || null,

		items: (data.items || []).map(item => ({
			id: item.extras?.id || String(item.item_id),
			itemId: item.item_id,
			name: item.name,
			code: item.extras?.code || '',
			quantity: item.quantity,
			unit: item.unit_code,
			price: item.price,
			taxes: item.extras?.taxes || [],
			discount: item.extras?.discount?.[0] || {
				type: 'percentage',
				value: 0,
				comment: null
			}
		})),

		paymentPurpose: data.paymentPurpose || '',
		partialPayment: Boolean(data.partialPayment),
		note: data.note || '',

		transactions: (data.transactions || []).map(tx => ({
			...tx,
			amount: tx?.amount,
		})),

		totalAmount: (data.totals?.totalAmount || 0) / 100,
		paidAmount: (data.totals?.paidAmount || 0) / 100,
		amountDue:
			((data.totals?.totalAmount || 0) -
				(data.totals?.paidAmount || 0)) / 100,

		totals: {
			totalAmount: data.totals.totalAmount,
			paidAmount: data.totals.paidAmount,
			refundAmount: data.totals.refundAmount
		}
	}
}

export function mapInvoiceStatsFromApi(apiStats) {
	return Object.values(InvoiceStatus).map(frontStatus => {
		const backendKey = frontStatus.toUpperCase()

		const stat = apiStats[backendKey] || {}

		return {
			status: frontStatus,
			count: stat.count || 0,
			totalAmount: stat.total_amount || 0,
			totalPaid: stat.total_pay_amount || 0,
			totalRefund: stat.total_refund_amount || 0,
		}
	})
}

export function mapInvoiceToApi(invoice, partnerUuid, partnerToken) {
	const payload= {
		sender: {
			partner_uuid: partnerUuid,
			partner_token: partnerToken,
		},

		number: invoice.number,
		status: invoice.status.toUpperCase(),

		type: INVOICE_TYPE_ABBREVIATIONS[invoice.type],
		dic_bill_type_id: BILL_TYPE_CODE_TO_ID[
			INVOICE_TYPE_ABBREVIATIONS[invoice.type]
		],

		date: invoice.date,
		dueDate: invoice.dueDate,

		currency: invoice.currency,

		senderName: invoice.senderName || '',
		senderDetails: invoice.senderDetails || '',

		recipient: normalizeRecipient(invoice.recipient),

		items: invoice.items.map(item => {
			const grossCents = item.quantity * item.price

			const d = item.discount || { type: 'percentage', value: 0, comment: null }

			const discountCents =
				d.value > 0
					? d.type === 'percentage'
						? Math.round(grossCents * d.value / 100)
						: Math.round(d.value * 100)
					: 0

			const netCents = Math.max(0, grossCents - discountCents)

			const taxCents = (item.taxes || []).reduce(
				(sum, t) => sum + Math.round(netCents * t.rate / 100),
				0
			)

			return {
				id: item.id || 'init_item',
				name: item.name,
				code: item.code || null,
				quantity: item.quantity,
				unit: item.unit || 'pcs',
				price: item.price,
				taxes: item.taxes,

				discount: [
					{
						type: d.type,
						value: d.type === 'fixed'
							? discountCents
							: d.value,
						comment: d.comment || null,
					}
				],

				finalAmmount: netCents + taxCents,
			}
		}),

		paymentPurpose: invoice.paymentPurpose || '',
		partialPayment: Boolean(invoice.partialPayment),
		note: invoice.note || '',

		transactions: invoice.transactions || [],
	}

	if (invoice.id) {
		payload.id = invoice.id
		payload.invoiceId = invoice.id
	}

	return payload
}

function normalizeRecipient(recipient) {
	if (!recipient) return { type: 'on_payment' }

	const type = String(recipient.type || '').toLowerCase()

	if (type === 'individual') {
		const p = recipient.person || {}

		return {
			type: 'individual',
			person: {
				firstName: p.firstName || '',
				lastName: p.lastName || '',
				middleName: p.middleName || '',

				phone: p.phone || '',
				email: p.email || '',

				RNOKPP: p.RNOKPP || '',
				passport: p.passport || '',
				birthDate: p.birthDate || '',

				address: p.address || '',
				city: p.city || '',
				country: p.country || '',
				postCode: p.postCode || '',
				state: p.state || '',
			},
		}
	}

	if (type === 'entity') {
		const l = recipient.legal || {}

		return {
			type: 'entity',
			legal: {
				name: l.name || '',
				EDRPOU: l.EDRPOU || '',

				IBAN: l.IBAN || '',
				MFO: l.MFO || '',
				vat: Boolean(l.vat),

				actualCountry: l.actualCountry || '',
				country: l.country || '',
				city: l.city || '',
				street: l.street || '',
				building: l.building || '',
			},
		}
	}

	return { type: 'on_payment' }
}




